import { requiredAmountForMinBuy } from "@repo/shared/types";
import { ArrowLeft, Pencil, Trash2 } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import type { SearchableSelectOption } from "@/components/ui/searchable-select";
import { SectionHeader } from "@/components/ui/section-header";
import {
  Table,
  TableActionsCell,
  TableActionsHead,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";

import { formatWeightTotal } from "./format-weight-total";
import { ProductWeightDialog } from "./product-weight-dialog";
import { T } from "./products-weights-editor.i18n";

/** One product a portfolio holds, as the editor shows it. */
export type WeightEditorRow = {
  /** Id of the portfolio-product row (not of the product). */
  id: string;
  productId: string;
  name: string;
  isin: string | null;
  weight: number;
  minBuyAmount: number | null;
  /** Wrapper types of the product in preference order; the first is the default. */
  wrapperNames: string[];
};

type ProductsWeightsEditorProps = {
  backTo: string;
  backLabel: string;
  /** Names the portfolio above the table when it has no other header. */
  heading?: string;
  /** Portfolio-level settings shown above the products. */
  settings?: ReactNode;
  rows: WeightEditorRow[];
  /** Products that can still be added. */
  productOptions: SearchableSelectOption[];
  /** Shows the wrapper types column (the collection has the feature enabled). */
  showWrapperTypes: boolean;
  totalWeight: number;
  isComplete: boolean;
  /** Step of the weight input. */
  weightStep: string;
  onAdd: (productId: string, weight: number) => Promise<unknown>;
  onUpdate: (rowId: string, weight: number) => Promise<unknown>;
  onRemove: (rowId: string) => Promise<unknown>;
};

/**
 * The products of one portfolio (model or dynamic) with their weights. Every
 * add, weight change and removal is saved on its own; the total may stay below
 * 100% and is flagged until it gets there.
 */
export function ProductsWeightsEditor({
  backTo,
  backLabel,
  heading,
  settings,
  rows,
  productOptions,
  showWrapperTypes,
  totalWeight,
  isComplete,
  weightStep,
  onAdd,
  onUpdate,
  onRemove,
}: ProductsWeightsEditorProps) {
  const { formatNumber } = useRegionalFormat();
  const t = useT(T);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [rowToDelete, setRowToDelete] = useState<string | null>(null);

  const editingRow = rows.find((row) => row.id === editingRowId) ?? null;

  // QP-193 — a product's minimum buy implies a portfolio-level minimum at its
  // weight (min / weight × 100). The binding product is the one demanding the
  // largest portfolio amount.
  const hasAnyMinBuy = rows.some((row) => (row.minBuyAmount ?? 0) > 0);
  const minInvestment = rows.reduce<{ amount: number; row: WeightEditorRow } | null>((acc, row) => {
    if (!row.minBuyAmount || row.minBuyAmount <= 0) return acc;
    const required = requiredAmountForMinBuy(row.minBuyAmount, row.weight);
    if (required == null) return acc;
    return acc && acc.amount >= required ? acc : { amount: required, row };
  }, null);

  const handleDelete = async () => {
    if (!rowToDelete) return;
    try {
      await onRemove(rowToDelete);
    } catch (err) {
      toast.error((err instanceof Error && err.message) || t.failedToDelete);
    } finally {
      setRowToDelete(null);
    }
  };

  return (
    <div className="space-y-4">
      <Link
        to={backTo}
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        {backLabel}
      </Link>

      <div className="space-y-4">
        {heading && <p className="text-sm text-muted-foreground">{heading}</p>}
        {settings}
        <SectionHeader
          title={t.productsTitle}
          action={
            <Button
              onClick={() => {
                setEditingRowId(null);
                setDialogOpen(true);
              }}
            >
              {t.addProduct}
            </Button>
          }
        />

        {rows.length === 0 ? (
          <EmptyState>{t.noProducts}</EmptyState>
        ) : (
          <Table variant="data">
            <TableHeader>
              <TableRow>
                <TableHead>{t.product}</TableHead>
                <TableHead>{t.isin}</TableHead>
                {showWrapperTypes && <TableHead>{t.wrapperTypes}</TableHead>}
                {hasAnyMinBuy && <TableHead className="text-right">{t.minBuy}</TableHead>}
                <TableHead className="text-right">{t.weight}</TableHead>
                <TableActionsHead>{t.actions}</TableActionsHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-medium">{row.name}</TableCell>
                  <TableCell>
                    <code className="text-xs bg-muted px-2 py-1 rounded">{row.isin}</code>
                  </TableCell>
                  {showWrapperTypes && (
                    <TableCell>
                      {row.wrapperNames.length === 0 ? (
                        "—"
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {row.wrapperNames.map((name, i) => (
                            <Badge key={name} variant={i === 0 ? "secondary" : "outline"}>
                              {name}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </TableCell>
                  )}
                  {hasAnyMinBuy && (
                    <TableCell className="text-right">
                      {(row.minBuyAmount ?? 0) > 0 ? formatNumber(row.minBuyAmount as number) : "—"}
                    </TableCell>
                  )}
                  <TableCell className="font-bold text-right">
                    {formatNumber(row.weight, { maximumFractionDigits: 4 })}%
                  </TableCell>
                  <TableActionsCell>
                    <Button
                      size="icon"
                      tooltip={t.editDescription}
                      variant="ghost"
                      onClick={() => {
                        setEditingRowId(row.id);
                        setDialogOpen(true);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      tooltip={t.delete}
                      variant="ghost"
                      onClick={() => setRowToDelete(row.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableActionsCell>
                </TableRow>
              ))}
              <TableRow className="border-t-2">
                <TableCell colSpan={showWrapperTypes ? 3 : 2} className="font-medium text-right">
                  {t.total}
                </TableCell>
                {hasAnyMinBuy && (
                  <TableCell className="font-bold text-right">
                    {minInvestment ? formatNumber(Math.ceil(minInvestment.amount)) : "—"}
                  </TableCell>
                )}
                <TableCell
                  className={`font-bold text-right ${isComplete ? "" : "text-destructive"}`}
                >
                  {formatWeightTotal(totalWeight, formatNumber)}%
                </TableCell>
                <TableCell>
                  {!isComplete && (
                    <span className="text-xs text-destructive">{t.mustTotal100}</span>
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}

        {minInvestment && (
          <p className="text-sm text-muted-foreground">
            {t.minimumInvestment(
              formatNumber(Math.ceil(minInvestment.amount)),
              minInvestment.row.name,
            )}
          </p>
        )}
      </div>

      <ProductWeightDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        editing={editingRow}
        productOptions={productOptions}
        totalWeight={totalWeight}
        weightStep={weightStep}
        onAdd={onAdd}
        onUpdate={onUpdate}
      />

      <ConfirmDialog
        open={rowToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setRowToDelete(null);
        }}
        title={t.deleteProductTitle}
        description={t.deleteProductDescription}
        confirmText={t.delete}
        cancelText={t.cancel}
        onConfirm={handleDelete}
        variant="destructive"
      />
    </div>
  );
}
